import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, addDays, nextMonday, nextWednesday, nextFriday } from "date-fns";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarIcon, ArrowRight, ArrowLeft, Check, Zap, Truck, Droplet, CreditCard, Banknote, Package } from "lucide-react";

const PICKUP_WEEKDAYS = [1, 3, 5]; // Mon=1, Wed=3, Fri=5 (JS: Sun=0)
const DROPOFF_LABELS = { 1: "Tuesday", 3: "Thursday", 5: "Saturday" }; // pickup day → dropoff day label
const DROPOFF_INDEX = { 1: 2, 3: 4, 5: 6 }; // pickup weekday → dropoff weekday

function nextValidPickup() {
  const today = new Date();
  for (let i = 1; i <= 7; i++) {
    const d = addDays(today, i);
    if (PICKUP_WEEKDAYS.includes(d.getDay())) return d;
  }
  return nextMonday(today);
}

export default function Book() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [config, setConfig] = useState(null);
  const [referral, setReferral] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    service_tier: "Wash & Fold",
    pickup_date: nextValidPickup(),
    weight_lbs: 25,
    oversized_items: 0,
    addons: {},
    detergent_source: "Foldora provides",
    express: false,
    detergent: "Fresh Scent",
    address: "",
    phone: "",
    notes: "",
    payment_method: "stripe",
    apply_credits: true,
    agreement_accepted: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data));
    api.get("/referrals/me").then((r) => setReferral(r.data)).catch(() => {});
  }, []);

  const subtotal = useMemo(() => {
    if (!config) return 0;
    let s = config.base_price || 50;
    if ((form.weight_lbs || 0) > (config.included_lbs || 25)) {
      s += ((form.weight_lbs || 0) - (config.included_lbs || 25)) * config.price_per_lb;
    }
    s += (form.oversized_items || 0) * (config.oversized_item_price || 1);
    (form.addons ? Object.entries(form.addons) : []).forEach(([name, qty]) => {
      const found = (config.addons || []).find((x) => x.name === name);
      if (found) s += found.price * (parseInt(qty, 10) || 0);
    });
    if (form.express) s += config.express_surcharge || 15;
    return s;
  }, [form, config]);

  const creditsToApply = useMemo(() => {
    if (!form.apply_credits || !referral || !referral.credits) return 0;
    return Math.min(referral.credits, subtotal);
  }, [form.apply_credits, referral, subtotal]);

  const total = useMemo(() => {
    const t = subtotal - creditsToApply;
    return t.toFixed(2);
  }, [subtotal, creditsToApply]);

  const dropoffDate = useMemo(() => {
    if (form.express) return form.pickup_date;
    const wd = form.pickup_date.getDay();
    const target = DROPOFF_INDEX[wd];
    let d = addDays(form.pickup_date, 1);
    while (d.getDay() !== target) d = addDays(d, 1);
    return d;
  }, [form.pickup_date, form.express]);

  if (loading || !user || !config) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const next = () => setStep((s) => Math.min(s + 1, 5));
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const submit = async () => {
    if (!form.address || !form.phone) {
      toast.error("Address and phone are required");
      return;
    }
    if (!form.weight_lbs || form.weight_lbs <= 0) {
      toast.error("Please enter estimated weight");
      return;
    }
    if (!form.agreement_accepted) {
      toast.error("Please accept the Service Agreement to continue");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        service_tier: form.service_tier,
        pickup_date: format(form.pickup_date, "yyyy-MM-dd"),
        weight_lbs: parseFloat(form.weight_lbs),
        oversized_items: parseInt(form.oversized_items || 0, 10),
        addons: form.addons || {},
        detergent_source: form.detergent_source,
        express: form.express,
        detergent: form.detergent,
        address: form.address,
        phone: form.phone,
        notes: form.notes,
        payment_method: form.payment_method,
        apply_credits: !!form.apply_credits,
        agreement_accepted: true,
        agreement_version: config.service_agreement_version,
      };
      const { data: booking } = await api.post("/bookings", payload);
      toast.success("Booking created!");
      if (form.payment_method === "stripe") {
        const { data: sess } = await api.post("/payments/checkout/session", {
          booking_id: booking.booking_id,
          origin_url: window.location.origin,
        });
        window.location.href = sess.url;
      } else if (form.payment_method === "paypal") {
        const { data: order } = await api.post("/payments/paypal/create-order", {
          booking_id: booking.booking_id,
          origin_url: window.location.origin,
        });
        window.location.href = order.url;
      } else {
        navigate(`/booking/${booking.booking_id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  const steps = ["Service", "Pickup", "Detergent", "Address", "Payment"];

  return (
    <div data-testid="book-page" className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-pink-500">New booking</div>
            <h1 className="font-heading text-4xl font-black tracking-tighter text-stone-900">Schedule your pickup</h1>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            {steps.map((label, i) => {
              const idx = i + 1;
              const active = idx === step;
              const done = idx < step;
              return (
                <React.Fragment key={label}>
                  <div
                    data-testid={`step-indicator-${idx}`}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black ${
                      done ? "bg-pink-500 text-white" : active ? "bg-[#4B6043] text-white" : "bg-stone-200 text-stone-500"
                    }`}
                  >
                    {done ? <Check className="w-4 h-4" /> : idx}
                  </div>
                  {i < steps.length - 1 && <div className={`w-6 h-px ${done ? "bg-pink-500" : "bg-stone-200"}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="p-8 rounded-3xl border border-stone-200 bg-white">
              {step === 1 && (
                <div>
                  <h2 className="font-heading text-2xl font-black tracking-tighter text-stone-900">1 · Pick your service</h2>
                  <p className="text-stone-500 mt-1">Choose the treatment your laundry needs. Flat rate: <b>${config.price_per_lb}/lb</b> · <b>${config.minimum_order} minimum</b>.</p>
                  <div className="mt-6 grid sm:grid-cols-3 gap-4">
                    {config.service_tiers.map((name) => (
                      <button
                        key={name}
                        data-testid={`service-select-${name.toLowerCase().replace(/\W/g, "-")}`}
                        type="button"
                        onClick={() => update("service_tier", name)}
                        className={`text-left rounded-2xl p-5 border-2 transition-all ${
                          form.service_tier === name ? "border-pink-500 bg-pink-50" : "border-stone-200 bg-white hover:border-stone-300"
                        }`}
                      >
                        <div className="font-heading font-bold text-lg text-stone-900">{name}</div>
                        <div className="text-pink-600 font-bold text-sm">${config.price_per_lb}/lb</div>
                      </button>
                    ))}
                  </div>

                  <div className="mt-8">
                    <Label htmlFor="weight" className="flex items-center gap-2"><Package className="w-4 h-4" /> Estimated weight (lbs)</Label>
                    <div className="mt-2 flex items-center gap-3">
                      <Input
                        data-testid="weight-input"
                        id="weight"
                        type="number"
                        min="1"
                        step="0.5"
                        value={form.weight_lbs}
                        onChange={(e) => update("weight_lbs", e.target.value)}
                        className="h-12 rounded-xl max-w-xs"
                      />
                      <div className="text-sm text-stone-500">First {config.included_lbs} lbs included in ${config.base_price} · then ${config.price_per_lb}/lb</div>
                    </div>
                    <p className="text-xs text-stone-500 mt-2">${config.minimum_order} minimum applies · we&rsquo;ll weigh on pickup and adjust the actual weight.</p>
                  </div>

                  <div className="mt-6 grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label className="flex items-center gap-2">Oversized items (comforters, drapes)</Label>
                      <div className="mt-2 flex items-center gap-2">
                        <Button type="button" variant="outline" data-testid="oversized-dec" className="rounded-full h-10 w-10 p-0" onClick={() => update("oversized_items", Math.max(0, (parseInt(form.oversized_items,10)||0) - 1))}>-</Button>
                        <Input data-testid="oversized-input" type="number" min="0" value={form.oversized_items} onChange={(e) => update("oversized_items", e.target.value)} className="h-10 rounded-xl w-24 text-center" />
                        <Button type="button" variant="outline" data-testid="oversized-inc" className="rounded-full h-10 w-10 p-0" onClick={() => update("oversized_items", (parseInt(form.oversized_items,10)||0) + 1)}>+</Button>
                        <span className="text-sm text-stone-500 ml-2">+${config.oversized_item_price} each</span>
                      </div>
                    </div>
                    <div>
                      <Label>Add-ons ($5 per item)</Label>
                      <div className="mt-2 space-y-2">
                        {(config.addons || []).map((a) => {
                          const qty = parseInt((form.addons || {})[a.name], 10) || 0;
                          const setQty = (n) => update("addons", { ...(form.addons || {}), [a.name]: Math.max(0, n) });
                          return (
                            <div
                              key={a.name}
                              data-testid={`addon-${a.name.toLowerCase()}`}
                              className={`rounded-2xl p-3 border-2 flex items-center justify-between ${qty > 0 ? "border-pink-500 bg-pink-50" : "border-stone-200 bg-white"}`}
                            >
                              <div>
                                <div className="font-heading font-bold text-stone-900 text-sm">{a.name}</div>
                                <div className="text-xs text-stone-500">+${a.price} per item</div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button type="button" data-testid={`addon-${a.name.toLowerCase()}-dec`} variant="outline" className="rounded-full h-8 w-8 p-0" onClick={() => setQty(qty - 1)}>-</Button>
                                <div className="w-8 text-center font-heading font-black text-stone-900">{qty}</div>
                                <Button type="button" data-testid={`addon-${a.name.toLowerCase()}-inc`} variant="outline" className="rounded-full h-8 w-8 p-0" onClick={() => setQty(qty + 1)}>+</Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div>
                  <h2 className="font-heading text-2xl font-black tracking-tighter text-stone-900">2 · Pickup schedule</h2>
                  <p className="text-stone-500 mt-1">Pickups on <b>Mon / Wed / Fri, 9 – 11 AM</b>. Drop-off on <b>Tue / Thu / Sat, 3 – 6 PM</b>.</p>
                  <div className="mt-6 grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label>Pickup date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            data-testid="pickup-date-btn"
                            variant="outline"
                            className="mt-1 w-full h-12 rounded-xl justify-start font-semibold"
                          >
                            <CalendarIcon className="w-4 h-4 mr-2 text-pink-500" />
                            {format(form.pickup_date, "EEE, MMM d")}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 w-auto" align="start">
                          <Calendar
                            mode="single"
                            selected={form.pickup_date}
                            onSelect={(d) => d && update("pickup_date", d)}
                            disabled={(d) => {
                              const today = new Date(); today.setHours(0,0,0,0);
                              return d < today || !PICKUP_WEEKDAYS.includes(d.getDay());
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <div className="text-xs text-stone-500 mt-2">Pickup window: {config.pickup_window}</div>
                    </div>
                    <div>
                      <Label>Drop-off</Label>
                      <div className="mt-1 h-12 rounded-xl border border-stone-200 bg-stone-50 px-4 flex items-center gap-2 text-stone-900 font-semibold">
                        <Truck className="w-4 h-4 text-pink-500" />
                        {format(dropoffDate, "EEE, MMM d")}
                      </div>
                      <div className="text-xs text-stone-500 mt-2">Drop-off window: {config.dropoff_window}</div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <Label className="mb-3 block">Delivery speed</Label>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <button
                        data-testid="delivery-standard-btn"
                        type="button"
                        onClick={() => update("express", false)}
                        className={`text-left rounded-2xl p-5 border-2 ${!form.express ? "border-pink-500 bg-pink-50" : "border-stone-200"}`}
                      >
                        <div className="flex items-center gap-2 font-heading font-bold text-lg text-stone-900">
                          <Truck className="w-5 h-5 text-pink-500" /> Standard
                        </div>
                        <div className="text-sm text-stone-600 mt-1">Drop-off next Tue/Thu/Sat · $2/lb · $50 min</div>
                      </button>
                      <button
                        data-testid="delivery-express-btn"
                        type="button"
                        onClick={() => update("express", true)}
                        className={`text-left rounded-2xl p-5 border-2 ${form.express ? "border-pink-500 bg-pink-50" : "border-stone-200"}`}
                      >
                        <div className="flex items-center gap-2 font-heading font-bold text-lg text-stone-900">
                          <Zap className="w-5 h-5 text-pink-500" /> Express <span className="text-pink-600 text-sm">+$15</span>
                        </div>
                        <div className="text-sm text-stone-600 mt-1">Same-day return · priority handling</div>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div>
                  <h2 className="font-heading text-2xl font-black tracking-tighter text-stone-900">3 · Detergent</h2>
                  <p className="text-stone-500 mt-1">Who provides the detergent?</p>
                  <div className="mt-4 grid sm:grid-cols-2 gap-3">
                    {(config.detergent_sources || ["Foldora provides", "Customer supplies"]).map((src) => (
                      <button
                        key={src}
                        type="button"
                        data-testid={`detergent-source-${src.toLowerCase().replace(/\W+/g, "-")}`}
                        onClick={() => update("detergent_source", src)}
                        className={`text-left rounded-2xl p-4 border-2 ${form.detergent_source === src ? "border-pink-500 bg-pink-50" : "border-stone-200"}`}
                      >
                        <div className="font-heading font-bold text-stone-900">{src}</div>
                        <div className="text-xs text-stone-500 mt-1">{src === "Foldora provides" ? "Pick from our 5 options below" : "Leave detergent with your bag — we&rsquo;ll use only that"}</div>
                      </button>
                    ))}
                  </div>
                  {form.detergent_source === "Foldora provides" && (
                    <>
                      <p className="text-stone-500 mt-6 text-sm">Choose your detergent:</p>
                      <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {(config.detergents_detail || config.detergents.map((n) => ({ name: n }))).map((d) => (
                          <button
                            key={d.name}
                            data-testid={`detergent-${d.name.toLowerCase().replace(/\W+/g, "-")}`}
                            type="button"
                            onClick={() => update("detergent", d.name)}
                            className={`text-left rounded-2xl p-3 border-2 transition-all ${form.detergent === d.name ? "border-pink-500 bg-pink-50" : "border-stone-200 bg-white hover:border-stone-300"}`}
                          >
                            <div className="rounded-xl bg-stone-50 h-24 flex items-center justify-center overflow-hidden">
                              {d.image_url ? (
                                <img src={d.image_url} alt={d.name} className="max-h-full max-w-full object-contain" />
                              ) : (
                                <Droplet className="w-8 h-8 text-pink-500" />
                              )}
                            </div>
                            <div className="mt-2 font-heading font-bold text-stone-900 text-sm">{d.name}</div>
                            {d.description && <div className="text-xs text-stone-500 mt-0.5 leading-snug">{d.description}</div>}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {step === 4 && (
                <div>
                  <h2 className="font-heading text-2xl font-black tracking-tighter text-stone-900">4 · Pickup address</h2>
                  <p className="text-stone-500 mt-1">Where should we come?</p>
                  <div className="mt-6 space-y-4">
                    <div>
                      <Label htmlFor="address">Address</Label>
                      <Textarea data-testid="address-input" id="address" rows={3} value={form.address}
                        onChange={(e) => update("address", e.target.value)}
                        placeholder="Street, apt, city, ZIP" className="mt-1 rounded-xl" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input data-testid="phone-input" id="phone" value={form.phone}
                          onChange={(e) => update("phone", e.target.value)}
                          placeholder="+1 555 000 0000" className="mt-1 h-12 rounded-xl" />
                      </div>
                      <div>
                        <Label htmlFor="notes">Notes (optional)</Label>
                        <Input data-testid="notes-input" id="notes" value={form.notes}
                          onChange={(e) => update("notes", e.target.value)}
                          placeholder="Ring doorbell, buzzer #12" className="mt-1 h-12 rounded-xl" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step === 5 && (
                <div>
                  <h2 className="font-heading text-2xl font-black tracking-tighter text-stone-900">5 · Payment</h2>
                  <p className="text-stone-500 mt-1">How would you like to pay?</p>
                  <RadioGroup value={form.payment_method} onValueChange={(v) => update("payment_method", v)}
                    className="mt-6 grid sm:grid-cols-2 gap-4">
                    <label data-testid="pay-stripe"
                      className={`cursor-pointer rounded-2xl p-5 border-2 ${form.payment_method === "stripe" ? "border-pink-500 bg-pink-50" : "border-stone-200"}`}>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="stripe" id="pay-stripe" />
                        <CreditCard className="w-5 h-5 text-pink-500" />
                        <div className="font-heading font-bold text-stone-900">Card online (Stripe)</div>
                      </div>
                      <div className="text-sm text-stone-600 mt-2 ml-8">Debit or credit card · secure checkout now</div>
                    </label>
                    <label data-testid="pay-paypal"
                      className={`cursor-pointer rounded-2xl p-5 border-2 ${form.payment_method === "paypal" ? "border-pink-500 bg-pink-50" : "border-stone-200"}`}>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="paypal" id="pay-paypal" />
                        <img alt="PayPal" src="https://cdn.simpleicons.org/paypal/003087" className="w-5 h-5" />
                        <div className="font-heading font-bold text-stone-900">PayPal</div>
                      </div>
                      <div className="text-sm text-stone-600 mt-2 ml-8">Pay with your PayPal balance or linked card.</div>
                    </label>
                    <label data-testid="pay-card-at-door"
                      className={`cursor-pointer rounded-2xl p-5 border-2 ${form.payment_method === "card_at_door" ? "border-pink-500 bg-pink-50" : "border-stone-200"}`}>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="card_at_door" id="pay-card-at-door" />
                        <CreditCard className="w-5 h-5 text-pink-500" />
                        <div className="font-heading font-bold text-stone-900">Card at door</div>
                      </div>
                      <div className="text-sm text-stone-600 mt-2 ml-8">Tap or insert debit/credit on our terminal at pickup.</div>
                    </label>
                    <label data-testid="pay-cod"
                      className={`cursor-pointer rounded-2xl p-5 border-2 ${form.payment_method === "cod" ? "border-pink-500 bg-pink-50" : "border-stone-200"}`}>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="cod" id="pay-cod" />
                        <Banknote className="w-5 h-5 text-pink-500" />
                        <div className="font-heading font-bold text-stone-900">Cash at door</div>
                      </div>
                      <div className="text-sm text-stone-600 mt-2 ml-8">Pay in cash when we return your clothes.</div>
                    </label>
                  </RadioGroup>

                  <div className="mt-8 border-t border-stone-200 pt-6">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        data-testid="agreement-checkbox"
                        type="checkbox"
                        checked={form.agreement_accepted}
                        onChange={(e) => update("agreement_accepted", e.target.checked)}
                        className="w-5 h-5 mt-0.5 accent-pink-500"
                      />
                      <span className="text-sm text-stone-700">
                        I have read and accept the{" "}
                        <a href="/legal/service-agreement" target="_blank" rel="noreferrer" data-testid="agreement-link" className="text-pink-600 font-semibold underline hover:text-pink-700">
                          Service Agreement
                        </a>
                        , including weight-based pricing, damage limits, and cancellation terms.
                      </span>
                    </label>
                  </div>
                </div>
              )}

              <div className="mt-10 flex items-center justify-between">
                <Button data-testid="back-btn" variant="ghost" onClick={back} disabled={step === 1} className="rounded-full font-bold">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                {step < 5 ? (
                  <Button data-testid="next-btn" onClick={next}
                    className="rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold h-12 px-6">
                    Continue <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button data-testid="submit-booking-btn" onClick={submit} disabled={submitting}
                    className="rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold h-12 px-6">
                    {submitting ? "Processing…" : `Confirm booking · $${total}`}
                  </Button>
                )}
              </div>
            </Card>
          </div>

          {/* Summary */}
          <div>
            <Card className="p-6 rounded-3xl border border-stone-200 bg-white sticky top-24">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-pink-500">Order summary</div>
              <div className="mt-4 space-y-3 text-sm">
                <Row k="Service" v={form.service_tier} />
                <Row k="Weight" v={`${form.weight_lbs || 0} lbs`} />
                <Row k="Pickup" v={`${format(form.pickup_date, "EEE, MMM d")} · ${config.pickup_window}`} />
                <Row k="Drop-off" v={`${format(dropoffDate, "EEE, MMM d")} · ${config.dropoff_window}`} />
                <Row k="Speed" v={form.express ? "Express (same day)" : "Standard"} />
                <Row k="Detergent" v={form.detergent} />
                <Row k="Payment" v={
                  form.payment_method === "stripe" ? "Card online" :
                  form.payment_method === "paypal" ? "PayPal" :
                  form.payment_method === "card_at_door" ? "Card at door" :
                  "Cash at door"
                } />
              </div>
              <div className="border-t border-stone-200 mt-5 pt-5 space-y-2 text-sm">
                <div className="flex justify-between text-stone-600">
                  <span>Base ({config.included_lbs} lbs incl.)</span>
                  <span>${config.base_price.toFixed(2)}</span>
                </div>
                {(form.weight_lbs || 0) > (config.included_lbs || 25) && (
                  <div className="flex justify-between text-stone-600">
                    <span>Extra {((form.weight_lbs || 0) - config.included_lbs).toFixed(1)} lbs × ${config.price_per_lb}</span>
                    <span>${(((form.weight_lbs || 0) - config.included_lbs) * config.price_per_lb).toFixed(2)}</span>
                  </div>
                )}
                {(form.oversized_items || 0) > 0 && (
                  <div className="flex justify-between text-stone-600">
                    <span>Oversized × {form.oversized_items}</span>
                    <span>${((form.oversized_items || 0) * config.oversized_item_price).toFixed(2)}</span>
                  </div>
                )}
                {Object.entries(form.addons || {}).map(([name, qty]) => {
                  const found = (config.addons || []).find((x) => x.name === name);
                  const q = parseInt(qty, 10) || 0;
                  return (found && q > 0) ? (
                    <div key={name} className="flex justify-between text-stone-600">
                      <span>{name} × {q}</span><span>${(found.price * q).toFixed(2)}</span>
                    </div>
                  ) : null;
                })}
                <div className="flex justify-between text-stone-600">
                  <span>Express</span>
                  <span>{form.express ? `$${config.express_surcharge.toFixed(2)}` : "$0.00"}</span>
                </div>
                {referral && referral.credits > 0 && (
                  <div className="pt-2 border-t border-pink-200">
                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-pink-700 font-semibold flex items-center gap-2">
                        <input
                          data-testid="apply-credits-toggle"
                          type="checkbox"
                          checked={!!form.apply_credits}
                          onChange={(e) => update("apply_credits", e.target.checked)}
                          className="w-4 h-4 accent-pink-500"
                        />
                        Apply ${referral.credits.toFixed(2)} credit
                      </span>
                      {creditsToApply > 0 && <span className="text-pink-600 font-bold">-${creditsToApply.toFixed(2)}</span>}
                    </label>
                  </div>
                )}
                <div className="flex justify-between text-stone-900 font-heading font-black text-2xl pt-1">
                  <span>Total</span><span data-testid="summary-total">${total}</span>
                </div>
                <p className="text-[10px] text-stone-400 italic mt-2">Applicable taxes (GST 5% + QST 9.975%) are added at checkout &amp; shown on your invoice.</p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-stone-500">{k}</span>
      <span className="text-stone-900 font-semibold text-right">{v}</span>
    </div>
  );
}
